import { FC, useEffect, useState } from "react";
import {
  inputConfiguration,
  selectConfiguration,
} from "../../../../../../utilities/inputPropsConfigurationFunctions";
import { Col, Input, InputDate, Row, Select } from "../../../../../TrueUI";
import { rowWithNoMarginNorGutter } from "../../../../../TrueUI/Grids/Row";
import AccountAndBalanceTable from "./AccountAndBalance/AccountAndBalanceTable";
import {
  getDataWithRowRecalculatedByNewAmount,
  getGetDataWithExactPayment,
} from "./ModalAddPaymentUtil";
import { BaseTable } from "../../../../../../dtos/base-table";
import style from "./AccountAndBalance/AccountAndBalanceSectionStyles.module.css";
import { ModalAddPaymentContentProps } from "./TypesAddPayment";
import { BaseGridSignatureEnum } from "../../../../../../dtos/base-grid-signature-enum";

const FormPaymentAndBalanceTable: FC<ModalAddPaymentContentProps> = ({
  paymentData,
  setPaymentData,
  paymentFormSelectsData,
  errorMessages,
  setBillingTransactionData,
  accountTableData,
  modalType = "newPayment",
  readOnly = false,
}) => {
  const isReadOnly = modalType === "editPayment" && readOnly;
  const [tableData, setTableData] = useState<BaseTable>(
    accountTableData?.tableData ?? {
      columns: [],
      data: [],
      signature: BaseGridSignatureEnum.TABLE_SIGNATURE_STANDARD,
    }
  );
  const [isTableReadyToRender, setIsTableReadyToRender] = useState(true);

  const updatePaymentAmountColumnValues = (data) => {
    const amount = paymentData?.amount ?? 0;
    if (data.length > 0) {
      const balanceIndex =
        accountTableData?.tableData?.columns.findIndex(
          (item) => item.fieldName === "Balance"
        ) ?? 0;
      const paymentIndex =
        accountTableData?.tableData?.columns.findIndex(
          (item) => item.fieldName === "Payment"
        ) ?? 0;
      const dataWithExactPaymentResult =
        amount > 0
          ? getGetDataWithExactPayment(
              getDataWithRowRecalculatedByNewAmount(
                [...data].reverse(),
                0,
                0,
                balanceIndex,
                paymentIndex
              ) ?? [],
              balanceIndex,
              paymentIndex,
              amount
            )
          : { dataUpdated: data, hasExactPayment: false };

      setIsTableReadyToRender(false);
      setTableData({
        ...tableData,
        data: dataWithExactPaymentResult?.hasExactPayment
          ? dataWithExactPaymentResult?.dataUpdated
          : getDataWithRowRecalculatedByNewAmount(
              data,
              0,
              amount,
              balanceIndex,
              paymentIndex
            ) ?? [],
      });
    }
  };

  useEffect(() => {
    updatePaymentAmountColumnValues(accountTableData?.tableData?.data ?? []);
  }, [paymentData?.amount]);

  useEffect(() => {
    if (tableData?.data?.length > 0 && isTableReadyToRender === false) {
      setIsTableReadyToRender(true);
    }
  }, [tableData]);

  return (
    <>
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <Select
            {...selectConfiguration(
              "id-bank-account",
              "bank-account",
              "Bank Account",
              isReadOnly
            )}
            labelFontType="BOLD_CAPTION"
            options={paymentFormSelectsData?.bankAccountsList ?? []}
            value={paymentData?.bankAccountId}
            onChange={(value) => {
              setPaymentData?.({ ...paymentData, bankAccountId: value });
            }}
            errorMessage={errorMessages?.bankAccountId}
          />
        </Col>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <Select
            {...selectConfiguration(
              "id-payment-type",
              "payment-type",
              "Payment Type",
              isReadOnly
            )}
            labelFontType="BOLD_CAPTION"
            options={paymentFormSelectsData?.paymentTypesList ?? []}
            value={paymentData?.paymentTypeId}
            onChange={(value) => {
              setPaymentData?.({ ...paymentData, paymentTypeId: value });
            }}
            errorMessage={errorMessages?.paymentTypeId}
          />
        </Col>
        <Col
          breakpoints={{ md: 4, lg: 4, xl: 4 }}
          horizontalAlign="flex-start"
        ></Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <InputDate
            id="id-payment-date"
            name="payment-date"
            label="Payment Date"
            labelFontType="BOLD_CAPTION"
            readOnly={isReadOnly}
            value={paymentData?.paymentDate}
            onChangeRawValue={(dateValue) =>
              setPaymentData?.({
                ...paymentData,
                paymentDate: dateValue,
              })
            }
            errorMessage={errorMessages?.paymentDate}
          />
        </Col>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <InputDate
            id="id-date-received"
            name="name-date-received"
            label="Date Received"
            labelFontType="BOLD_CAPTION"
            readOnly={isReadOnly}
            value={paymentData?.dateReceived}
            onChangeRawValue={(dateValue) =>
              setPaymentData?.({
                ...paymentData,
                dateReceived: dateValue,
              })
            }
            errorMessage={errorMessages?.dateReceived}
          />{" "}
        </Col>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <InputDate
            id="id-postmark-date"
            name="name-postmark-date"
            label="Postmark Date"
            labelFontType="BOLD_CAPTION"
            readOnly={isReadOnly}
            value={paymentData?.postmarkDate}
            onChangeRawValue={(dateValue) =>
              setPaymentData?.({
                ...paymentData,
                postmarkDate: dateValue,
              })
            }
            errorMessage={errorMessages?.postmarkDate}
          />{" "}
        </Col>
      </Row>
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <Input
            id={"id-amount"}
            name={"amount"}
            label={"Amount"}
            labelFontType={"BOLD_CAPTION"}
            readOnly={modalType === "editPayment"}
            prefix=""
            value={paymentData?.amount}
            maxNumericValue={99999999.99}
            inputFontType="BODY"
            allowNegatives={false}
            variant={"filled"}
            type={"fixedCurrency"}
            errorMessage={errorMessages?.amount}
            onChangeRawValue={(value) => {
              setPaymentData?.({
                ...paymentData,
                amount: value,
              });
            }}
          />
        </Col>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}>
          <Input
            {...inputConfiguration(
              "id-reference-number",
              "reference-number",
              "Check/Reference Number",
              50,
              isReadOnly
            )}
            type={"text"}
            prefix={""}
            value={paymentData?.referenceNumber}
            onChange={(value) =>
              setPaymentData?.({
                ...paymentData,
                referenceNumber: value.target.value,
              })
            }
            errorMessage={errorMessages?.referenceNumber}
            labelFontType={"BOLD_CAPTION"}
          />
        </Col>
        <Col breakpoints={{ md: 4, lg: 4, xl: 4 }}></Col>
      </Row>
      <div className={style.account_and_balance_table_section}>
        {isTableReadyToRender && (
          <AccountAndBalanceTable
            accountTableData={{
              ...accountTableData,
              tableData: tableData,
            }}
            errorDetails={errorMessages}
            tranAccountOptions={
              paymentFormSelectsData?.tranAccountOptions ?? []
            }
            setBillingTransactionData={setBillingTransactionData}
            readOnly={modalType === "editPayment"}
          />
        )}
      </div>
    </>
  );
};

export default FormPaymentAndBalanceTable;
